// The page editor (/pages/:id): full screen, outside the app Layout. Orbit's
// v2.62 editor (Flowmark layout) on NL-AOS: the top bar, the rail (steps,
// thank-you, sections), the canvas (desktop, tablet, phone; Edit or Try it),
// the inspector (Section, Page, AI), the History, Analytics and Share panels,
// undo and redo, draft autosave, and live pages that hold their edits until
// "Publish changes". Forms are CRM forms, calendars are CRM calendars.
//
// People who can only view (role readonly) get the same editor with nothing
// to change: no autosave, no publish, the settings panel disabled.
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../editor.css';
import { useAuth } from '../../auth';
import { setLeaveGuard } from '../../leave-guard';
import {
  normalizeContent, newSectionFilled, newStep, duplicateSection, moveItem, thankYouSections, syncLegacySections, resolvePageSeo, pageNeedsCalendar, pageNeedsForm, bookingNeedsCalendar, pageSteps,
  type LandingPage, type PageContent, type PageEdits, type PageStep, type PageTheme, type Section, type SectionType,
} from '../model/pages';
import { isValidSlug } from '../model/forms';
import { applyAiChanges } from '../model/page-ai';
import { ApiError, crmFormEditorPath, type CrmFormSummary, type DomainsResponse, type PageDomain, type SlugProblem } from '../api';
import { ChevronDownIcon, PaintbrushIcon, SparklesIcon } from '../icons';
import PageView, { type TryItOptions } from '../render/PageView';
import { FeedbackProvider, useConfirm, useToast } from '../ui/feedback';
import { PromptDialog } from '../ui/PromptDialog';
import { Loading } from '../ui/Loading';
import CanvasStage from './CanvasStage';
import { usePageHistory, snapshotKey, versionSnapshot, type PageSnapshot, type PageVersion } from './usePageHistory';
import { usePageSaver, type SaveMode, type WrittenRow } from './usePageSaver';
import { SectionTab, SectionTabEmpty } from './SectionTab';
import { PageTab } from './PageTab';
import { AiPanel } from './AiPanel';
import { SegGlide } from './SegGlide';
import { ADDABLE, TY_ADDABLE, AddSectionDialog } from './AddSection';
import type { CanvasMode, EditorDevice, InspectorTab } from './types';
import { EditorHeader, type SaveStatus } from './EditorHeader';
import { EditorRail } from './EditorRail';
import { EditorDrawer } from './EditorDrawer';
import { HistoryDrawer } from './HistoryDrawer';
import { ShareDrawer } from './ShareDrawer';
import { AnalyticsDrawer } from './AnalyticsDrawer';
import { PhoneBottomBar, PhoneEditPill } from './PhoneChrome';
import { typeLabelInline } from './sectionLabel';
import { EditorApiContext, realEditorApi, useEditorApi, type EditorApi } from './editorApi';
import { InspectorReadOnly } from './InspectorKit';
import { resolveLinkedForm } from './FormGroup';
import { resetSectionStylePatch, sectionStylePatch } from './sectionStyle';

// ── Canvas size ───────────────────────────────────────────────────────
// The first paint follows the screen (phone at 640px and under, tablet up to
// 1023px, desktop above); after that the last choice is remembered on this
// device.
export const DEVICE_KEY = 'nla:page-editor-device';
export function initialDevice(): EditorDevice {
  try {
    const v = localStorage.getItem(DEVICE_KEY);
    if (v === 'mobile' || v === 'phone') return 'phone';
    if (v === 'desktop' || v === 'tablet') return v;
  } catch { /* private mode */ }
  const w = typeof window !== 'undefined' ? window.innerWidth : 1440;
  return w <= 640 ? 'phone' : w <= 1023 ? 'tablet' : 'desktop';
}

/** The host the Page tab's link preview shows, remembered on this device. */
const PREVIEW_HOST_KEY = 'nla:pages:preview-host';
function readPreviewHost(): string {
  try { return localStorage.getItem(PREVIEW_HOST_KEY) ?? ''; } catch { return ''; }
}

/** Tablets and phones: the rail is a drawer and the settings a bottom sheet. */
const NARROW_QUERY = '(max-width: 1023px)';
function isNarrow(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.(NARROW_QUERY).matches;
}

/** A field being typed in keeps its own Escape. */
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  return !!el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT';
}

const INSPECTOR_TABS: { key: InspectorTab; label: string; icon?: ReactNode }[] = [
  { key: 'section', label: 'Section' },
  { key: 'page', label: 'Page', icon: <PaintbrushIcon size={13} /> },
  { key: 'ai', label: 'AI', icon: <SparklesIcon size={13} /> },
];

/** ⌘S / Ctrl+S (not ⇧, not ⌥). */
function isSaveCombo(e: KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key.toLowerCase() === 's' || e.code === 'KeyS');
}

/** Content as the editor works with it: steps always present (a step-less
 *  page becomes one step), content.sections mirroring step 1, and the
 *  thank-you as composable sections (synthesized from older fields). */
export function prepareContent(content: PageContent | null | undefined): PageContent {
  const c = content ?? {};
  const norm = normalizeContent(c, (n) => `s_load_${n}`);
  const ty = thankYouSections(c.thank_you, (n) => `ty_load_${n}`);
  return { ...c, sections: norm.steps[0]?.sections ?? norm.sections, steps: norm.steps, thank_you: { ...(c.thank_you ?? {}), sections: ty } };
}

/** A version's snapshot made ready for the editor (the same shape as a load). */
function prepareSnapshot(s: PageSnapshot): PageSnapshot {
  return { ...s, form_id: s.form_id ?? null, content: prepareContent(s.content), theme: s.theme ?? {} };
}

/** The link check's message under the Page link field. */
const SLUG_PROBLEM: Record<SlugProblem, string> = {
  invalid: 'Use lowercase letters, numbers and hyphens.',
  reserved: 'That link is reserved. Try another.',
  taken: 'Taken. Try another.',
};

/** The dev harness (PageEditorHarness): a stub API and, optionally, the view-only editor. */
export interface EditorHarness {
  api: EditorApi;
  readOnly?: boolean;
}

/** The /pages/:id route. Reads the id, and keys the editor by it: opening
 *  another page starts a fresh editor (the old one writes its pending draft
 *  edits as it unmounts). Reload (after the page changed somewhere else)
 *  starts a fresh one too. */
export default function PageEditor({ harness, pageId }: { harness?: EditorHarness; pageId?: string } = {}) {
  const params = useParams<{ id: string }>();
  const id = pageId ?? params.id;
  const [reloads, setReloads] = useState(0);
  return (
    <EditorApiContext.Provider value={harness?.api ?? realEditorApi}>
      <FeedbackProvider>
        <PageEditorBody key={`${id ?? ''}:${reloads}`} id={id} harness={harness} onReload={() => setReloads((n) => n + 1)} />
      </FeedbackProvider>
    </EditorApiContext.Provider>
  );
}

function PageEditorBody({ id, harness, onReload }: { id: string | undefined; harness?: EditorHarness; onReload: () => void }) {
  const navigate = useNavigate();
  const api = useEditorApi();
  const { user } = useAuth();
  // Writers are superadmins and execs; everyone else on staff can look.
  const canWrite = harness ? !harness.readOnly : user?.role === 'superadmin' || user?.role === 'exec';
  const readOnly = !canWrite;
  const canSetHomepage = harness ? !harness.readOnly : user?.role === 'superadmin';
  const confirm = useConfirm();
  const toast = useToast();
  const uid = useId();
  const [page, setPage] = useState<LandingPage | null>(null);
  const [loadFailed, setLoadFailed] = useState<'missing' | 'error' | null>(null);
  const [forms, setForms] = useState<CrmFormSummary[] | null>(null);
  const [domains, setDomains] = useState<DomainsResponse | null>(null);
  const history = usePageHistory(page, setPage, id, api, { prepare: prepareSnapshot, canWrite });
  // Result of the last availability check, for the slug it was run on.
  const [slugCheck, setSlugCheck] = useState<{ slug: string; available: boolean; reason?: SlugProblem } | null>(null);
  const slugReq = useRef(0);
  const [previewThanks, setPreviewThanks] = useState(false); // the thank-you view is on the canvas (rail "Thank you" row)
  const [previewBusy, setPreviewBusy] = useState(false); // minting the ?pt= token (Preview button)
  const [previewLink, setPreviewLink] = useState<string | null>(null);
  const previewRequest = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [formBusy, setFormBusy] = useState(false); // "New form" in the Form-section inspector
  // Side panels: Version history, Share, Analytics.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  // Below 1024px: the rail opens in a left drawer and the settings in a bottom sheet.
  const [railOpen, setRailOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('page'); // never blank: opens on the page design
  const [revealLink, setRevealLink] = useState(0); // bumped to open, scroll to and focus the Page tab's link field
  const [revealTracking, setRevealTracking] = useState(0);
  const [device, setDeviceState] = useState<EditorDevice>(initialDevice);
  const [mode, setMode] = useState<CanvasMode>('edit');
  const [selectedId, setSelectedId] = useState<string | null>(null); // selected section (canvas + rail)
  const [copiedStyle, setCopiedStyle] = useState<Partial<Section> | null>(null);
  const [addAnchor, setAddAnchor] = useState<{ after: string | null } | null>(null); // add-section dialog (insert after id; null = append)
  const [tplOpen, setTplOpen] = useState(false); // "Save as template" name prompt
  const [activeStep, setActiveStep] = useState(0); // which step is being edited + previewed
  const [renameIdx, setRenameIdx] = useState<number | null>(null); // the step being renamed (rail row menu)
  const [previewHost, setPreviewHostState] = useState(readPreviewHost);
  // Rail drag-and-drop: drag a section to reorder it, or a type out of the add
  // dialog to insert it. dragRef holds what's being dragged; dropAt is the
  // insertion index the drop line previews.
  const dragRef = useRef<{ kind: 'move'; index: number } | { kind: 'add'; type: SectionType } | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false); // a component/section drag is in progress (gates canvas drop targets)
  // The sections the last change touched (added, moved, duplicated, rewritten
  // by AI): they glow once on the canvas and in the rail. `n` restarts it.
  const [flash, setFlash] = useState<{ ids: string[]; n: number }>({ ids: [], n: 0 });
  const glow = (...ids: string[]) => setFlash((f) => ({ ids, n: f.n + 1 }));
  const idCtr = useRef(0);
  const mkId = () => `s_${Date.now().toString(36)}_${idCtr.current++}`;
  const historyOpenRef = useRef(historyOpen);
  historyOpenRef.current = historyOpen;
  // What the page holds now, as History compares it (only while the drawer is open).
  const currentSnapshotKey = useMemo(
    () => (historyOpen && page ? snapshotKey(prepareSnapshot(versionSnapshot(page))) : ''),
    [historyOpen, page],
  );

  function setDevice(d: EditorDevice) {
    setDeviceState(d);
    try { localStorage.setItem(DEVICE_KEY, d); } catch { /* private mode */ }
  }
  function setPreviewHost(h: string) {
    setPreviewHostState(h);
    try { localStorage.setItem(PREVIEW_HOST_KEY, h); } catch { /* private mode */ }
  }

  // ── Saving ──────────────────────────────────────────────────────────
  // Whether the page was live before the write that just landed (the publish
  // toast says "Published." or "Changes published.").
  const wasLiveRef = useRef(false);
  wasLiveRef.current = page?.status === 'published';
  const write = useCallback(async (pid: string, edits: PageEdits, saveMode: SaveMode, base: string | null): Promise<WrittenRow> => {
    const body = base ? { ...edits, base_updated_at: base } : { ...edits };
    if (saveMode === 'publish') return api.publishPage(pid, body);
    if (saveMode === 'unpublish') return api.unpublishPage(pid);
    return api.updatePage(pid, body);
  }, [api]);

  const saver = usePageSaver({
    page, setPage, write,
    autosave: canWrite,
    // A page needs a name, and a page link the user changed must be valid and
    // free, before anything is written. The saved link is never second-guessed
    // (an older page whose link predates today's rules still saves, and its
    // live URL keeps working).
    validate: (p, saved) => {
      if (!p.name.trim()) return 'Name the page first.';
      if (saved && p.slug === saved.slug) return null;
      if (!isValidSlug(p.slug) || (slugCheck?.slug === p.slug && !slugCheck.available)) return 'Fix the page link first.';
      return null;
    },
    onSaved: (written, saveMode, row) => {
      // Fields only the server changes (status, published_at, the form's
      // status, updated_at) follow the row it answered; the edits stay as they
      // are on screen (newer ones may have been made during the write).
      if (row) {
        setPage((cur) => (cur && cur.id === written.id ? {
          ...cur,
          updated_at: row.updated_at ?? cur.updated_at,
          published_at: row.published_at !== undefined ? row.published_at : cur.published_at,
          preview_token: row.preview_token ?? cur.preview_token,
          updated_by_name: row.updated_by_name ?? cur.updated_by_name,
          form: row.form !== undefined ? row.form : cur.form,
        } : cur));
      }
      if (saveMode === 'publish') {
        const fid = written.form_id;
        if (row?.form_went_live && fid != null) setForms((cur) => cur?.map((f) => (f.id === fid ? { ...f, status: 'live' } : f)) ?? cur);
        const formName = row?.form?.name ?? forms?.find((f) => f.id === fid)?.name ?? '';
        const done = row?.form_went_live
          ? `Published. The form${formName ? ` “${formName}”` : ''} is live now too.`
          : wasLiveRef.current ? 'Changes published.' : 'Published.';
        // Pieces visitors can't use yet: a Form section with no form, a booking section with no calendar.
        const missing = [
          pageNeedsForm(written.content, written.form_id) ? 'Pick a form for the Form section so visitors can sign up.' : '',
          pageNeedsCalendar(written.content) ? 'Pick a calendar for the booking section so visitors can book.' : '',
        ].filter(Boolean);
        if (missing.length) toast([done, ...missing].join(' '), 'info');
        else toast(done);
      }
      // The server keeps a version on publish, unpublish and (every 10 minutes) save.
      if (historyOpenRef.current) void history.loadVersions();
    },
    onStale: () => {
      toast('Someone saved this page in another tab or browser. Reload to get the latest.', 'error', { label: 'Reload', onAction: onReload });
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) { setLoadFailed('missing'); return; }
      try {
        const raw = await api.getPage(id);
        if (cancelled) return;
        setPage({ ...raw, content: prepareContent(raw.content), theme: raw.theme ?? {} });
      } catch (e) {
        if (!cancelled) setLoadFailed(e instanceof ApiError && e.status === 404 ? 'missing' : 'error');
        return;
      }
      // Every live and draft CRM form: a page made from a template links its
      // own draft form, which must show in the picker (it goes live when the
      // page is published).
      api.listCrmForms().then((list) => { if (!cancelled) setForms(Array.isArray(list) ? list : []); })
        .catch(() => { if (!cancelled) setForms([]); });
      api.listDomains().then((d) => { if (!cancelled) setDomains(d); }).catch(() => { /* links fall back to the app host */ });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Back from editing a form in the CRM (another tab): pick up its new name or status.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      api.listCrmForms().then((list) => { if (Array.isArray(list)) setForms(list); }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [api]);

  // Esc, in order: the add dialog, a side panel, the phone sheet, and only then
  // the selection. Fields being typed in, dialogs and the side panels handle
  // their own Esc first (the panels mark it handled).
  const escRef = useRef<(e: KeyboardEvent) => void>(() => {});
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) escRef.current(e); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ⌘S / Ctrl+S saves a draft now, or publishes a live page's changes, and
  // never opens the browser's own save dialog. Keys pressed inside the canvas
  // iframe reach the root's onKeyDownCapture instead (React carries them
  // through the portal); keys in this document land here.
  const shortcutRef = useRef<() => void>(() => {});
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isSaveCombo(e)) return;
      e.preventDefault();
      runShortcut(e.target);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Closing or reloading the tab with edits that aren't written asks first.
  const guardUnload = saver.dirty && canWrite;
  useEffect(() => {
    if (!guardUnload) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [guardUnload]);

  // In-app links (a sidebar link, Connect a domain) ask first when leaving
  // would lose edits: a live page's unpublished changes, or a draft that
  // cannot save. A draft that can save writes its edits as the editor closes.
  const pageLive = page?.status === 'published';
  const atRisk = canWrite && saver.dirty && (pageLive || !!saver.blocked || !!saver.error || saver.stale);
  useEffect(() => {
    setLeaveGuard(atRisk
      ? (pageLive ? 'Your changes aren’t live yet. Leave without publishing them?' : 'Your latest changes aren’t saved. Leave anyway?')
      : null);
  }, [atRisk, pageLive]);
  useEffect(() => () => setLeaveGuard(null), []);

  // Latest values for the long-lived key handlers above. saveShortcut and
  // handleEscape are function declarations further down (hoisted), so the
  // listeners always call the newest ones; they do nothing until the page has
  // loaded.
  const pageRef = useRef(page);
  useLayoutEffect(() => {
    pageRef.current = page;
    shortcutRef.current = saveShortcut;
    escRef.current = handleEscape;
  });

  // ── Selection and the inspector tab ─────────────────────────────────
  // Selecting a section (canvas or rail) shows its settings; clearing the
  // selection goes back to the page design (the AI tab stays where it is).
  // These only touch state, so the key handlers can call them at any time.
  function select(sid: string | null) {
    setSelectedId(sid);
    setInspectorTab((t) => (sid ? 'section' : t === 'section' ? 'page' : t));
  }
  // Clears the selection (and closes the phone settings sheet, if open).
  function deselect() {
    select(null);
    setSheetOpen(false);
  }
  function closePanels() {
    setHistoryOpen(false);
    setShareOpen(false);
    setAnalyticsOpen(false);
    setRailOpen(false);
  }
  function handleEscape(e: KeyboardEvent) {
    if (isTypingTarget(e.target)) return;
    if (tplOpen || renameIdx !== null) return; // they close themselves
    if (addAnchor) { setAddAnchor(null); return; }
    if (historyOpen || shareOpen || analyticsOpen || railOpen) { closePanels(); return; }
    if (sheetOpen) { setSheetOpen(false); return; }
    if (selectedId) deselect();
  }

  // ── Save, publish, leave (the shortcut runs before the page loads too) ──
  function saveShortcut() {
    const p = pageRef.current;
    if (!p || readOnly) return;
    const live = p.status === 'published';
    const linked = resolveLinkedForm(forms, p.form_id, p.form);
    if (live) { if (saver.isDirty() || linked?.status === 'draft') void publish(); }
    else if (saver.isDirty()) void saver.run('save');
  }
  // Inline canvas text commits on blur: commit it first so ⌘S saves what is
  // on screen, then save once React has applied the edit.
  function runShortcut(target: EventTarget | null) {
    const el = target as HTMLElement | null;
    if (el?.isContentEditable) { el.blur(); setTimeout(() => shortcutRef.current(), 0); return; }
    shortcutRef.current();
  }
  async function publish() {
    if (readOnly) return;
    const current = pageRef.current;
    if (current && pageNeedsCalendar(current.content)) {
      const currentSteps = pageSteps(current.content);
      const step = currentSteps.findIndex(item => item.sections.some(section => !section.hidden && bookingNeedsCalendar(section)));
      const missing = (step >= 0 ? currentSteps[step].sections : current.content.thank_you?.sections ?? []).find(section => !section.hidden && bookingNeedsCalendar(section));
      if (step >= 0) setActiveStep(step);
      setPreviewThanks(step < 0);
      setMode('edit');
      select(missing?.id ?? null);
      if (isNarrow()) setSheetOpen(true);
      toast('Choose a calendar for this booking section before publishing.', 'info');
      return;
    }
    if (saver.blocked) revealPageSettings();
    await saver.run('publish');
  }
  // Show where the name and link are fixed: the Page tab (the sheet on phones),
  // with the link field focused when that is the problem.
  function revealPageSettings() {
    setInspectorTab('page');
    if (isNarrow()) setSheetOpen(true);
    const err = saver.error;
    if (saver.blocked === 'Fix the page link first.' || err?.field === 'slug') setRevealLink((n) => n + 1);
    if (err?.field === 'analytics_tracking') setRevealTracking(Date.now());
  }

  if (loadFailed) {
    return (
      <div className="pe-scope">
        <div className="fixed inset-0 z-30 grid place-items-center bg-bg p-6 text-center" data-load-failed>
          <div className="grid justify-items-center gap-3">
            <p className="text-sm text-text">{loadFailed === 'missing' ? 'This page doesn’t exist anymore.' : 'This page couldn’t be loaded.'}</p>
            <div className="flex gap-2">
              {loadFailed === 'error' && <button type="button" className="btn-ghost btn-sm" onClick={onReload}>Try again</button>}
              <button type="button" className="btn-ghost btn-sm" onClick={() => navigate('/pages')}>Back to pages</button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!page) return <Loading fill label="Loading the page" />;
  const blocked = saver.blocked;
  // Links point at the slug that is actually saved (a changed link isn't
  // real until it saves, or on a live page until it is published).
  const savedSlug = saver.saved?.slug ?? page.slug;
  const slugChanged = !!saver.saved && page.slug !== saver.saved.slug;
  const slugInvalid = slugChanged && !isValidSlug(page.slug);
  const slugProblem = slugChanged && slugCheck?.slug === page.slug && !slugCheck.available ? (slugCheck.reason ?? 'taken') : null;
  const content = page.content;
  const steps = content.steps ?? [];
  const stepIdx = Math.min(activeStep, Math.max(0, steps.length - 1));
  const sections = steps[stepIdx]?.sections ?? content.sections ?? []; // active step's sections
  const tySections = content.thank_you?.sections ?? [];
  const theme = page.theme;
  const live = page.status === 'published';

  function patch(p: Partial<LandingPage>) { setPage((cur) => cur ? { ...cur, ...p } : cur); }
  function setContent(c: Partial<PageContent>) { setPage((cur) => cur ? { ...cur, content: { ...cur.content, ...c } } : cur); }
  function setTheme(t: Partial<PageTheme>) { setPage((cur) => cur ? { ...cur, theme: { ...cur.theme, ...t } } : cur); }
  // Steps are the source of truth; content.sections stays mirrored to step 1 so
  // thumbnails, templates, duplicates, and old published bundles keep working.
  function setSteps(next: PageStep[]) { setContent({ steps: next, sections: next[0]?.sections ?? [] }); }
  function setSections(next: Section[]) { setSteps(steps.map((st, i) => (i === stepIdx ? { ...st, sections: next } : st))); }
  function setTySections(next: Section[]) { setContent({ thank_you: { ...content.thank_you, sections: next } }); }

  // ── Step actions ────────────────────────────────────────────────────
  // The rail's row menu acts on any step (`i`); without one, the active step.
  function addStep() {
    const st = newStep(mkId(), `Step ${steps.length + 1}`, mkId);
    setSteps([...steps, st]);
    setActiveStep(steps.length);
    setPreviewThanks(false);
    select(null);
  }
  /** Keeps the canvas on the same step after the list changed around it. */
  function followActive(next: PageStep[]) {
    const activeId = steps[stepIdx]?.id;
    setActiveStep(Math.max(0, next.findIndex((st) => st.id === activeId)));
  }
  function moveStep(dir: -1 | 1, i = stepIdx) {
    const next = moveItem(steps, i, i + dir);
    if (next === steps) return;
    setSteps(next);
    followActive(next);
  }
  function dupStep(i = stepIdx) {
    const src = steps[i];
    if (!src) return;
    const copy: PageStep = { id: mkId(), name: `${src.name} (copy)`, sections: src.sections.map((sec) => duplicateSection(sec, mkId())) };
    setSteps([...steps.slice(0, i + 1), copy, ...steps.slice(i + 1)]);
    setActiveStep(i + 1);
    setPreviewThanks(false);
    select(null);
  }
  async function delStep(i = stepIdx) {
    if (steps.length <= 1) return; // never delete the last step
    const target = steps[i];
    if (!target) return;
    if (!(await confirm({ title: `Delete step “${target.name}”?`, body: 'Its sections will be removed from this page.', confirmLabel: 'Delete step', danger: true }))) return;
    const next = steps.filter((_, k) => k !== i);
    setSteps(next);
    if (i === stepIdx) {
      setActiveStep(Math.max(0, i - 1));
      select(null);
    } else {
      followActive(next);
    }
  }
  function renameStep(name: string) {
    const i = renameIdx ?? stepIdx;
    if (name) setSteps(steps.map((st, k) => (k === i ? { ...st, name } : st)));
    setRenameIdx(null);
  }

  // ── Canvas selection + section actions ──────────────────────────────
  // With the thank-you view on, the canvas (and all actions) target the
  // thank-you sections; otherwise the active step's. The shared footer is
  // selectable in both but never movable.
  const footerId = content.footer?.id ?? null;
  const visualSections = previewThanks ? tySections : sections;
  const setVisualSections = previewThanks ? setTySections : setSections;
  // The footer is added from the add dialog too, while a page has none (the
  // thank-you page reuses the page's footer).
  const canAddFooter = !previewThanks && !content.footer;
  const visualAddable: SectionType[] = previewThanks ? TY_ADDABLE : canAddFooter ? [...ADDABLE, 'footer'] : ADDABLE;
  const selIdx = selectedId ? visualSections.findIndex((sx) => sx.id === selectedId) : -1;
  const selectedIsFooter = selectedId !== null && selectedId === footerId;
  const selectedSection = selectedIsFooter ? content.footer ?? null : selIdx >= 0 ? visualSections[selIdx] : null;

  /** Phones and tablets: adding a section opens its settings right away. */
  function openSheetIfNarrow() { if (isNarrow()) setSheetOpen(true); }
  function openSheet(tab: InspectorTab) { setInspectorTab(tab); setSheetOpen(true); }

  /** The shared footer: added once, then selected. */
  function addFooter() {
    if (content.footer) { select(content.footer.id); return; }
    const f = newSectionFilled('footer', mkId());
    setContent({ footer: f });
    select(f.id);
    glow(f.id);
    openSheetIfNarrow();
  }
  // Drop onto the rail at insertion index `to` (0..length): reorder a dragged
  // section, or insert a type dragged out of the add dialog there.
  function dropSection(to: number) {
    const d = dragRef.current;
    dragRef.current = null;
    setDropAt(null);
    setDragging(false);
    if (!d || readOnly) return;
    if (d.kind === 'add') {
      setRailOpen(false);
      if (d.type === 'footer') { if (canAddFooter) addFooter(); return; }
      if (!visualAddable.includes(d.type)) return;
      const sec = newSectionFilled(d.type, mkId());
      setVisualSections([...visualSections.slice(0, to), sec, ...visualSections.slice(to)]);
      select(sec.id);
      glow(sec.id);
      openSheetIfNarrow();
      return;
    }
    // reorder: remove from d.index, insert before `to` (account for the shift)
    if (d.index === to || d.index + 1 === to) return; // dropped in place
    const arr = [...visualSections];
    const [item] = arr.splice(d.index, 1);
    arr.splice(d.index < to ? to - 1 : to, 0, item);
    setVisualSections(arr);
    if (item) glow(item.id);
  }
  // A rail row started being dragged (to reorder).
  function railDragStart(index: number) {
    dragRef.current = { kind: 'move', index };
    setDragging(true);
  }
  // A type from the add dialog started being dragged (to drop on the rail or the canvas).
  function paletteDragStart(type: SectionType) {
    dragRef.current = { kind: 'add', type };
    setDragging(true);
  }
  function dragDone() {
    dragRef.current = null;
    setDropAt(null);
    setDragging(false);
  }
  // A canvas section started being dragged (to reorder).
  function canvasDragStart(sid: string) {
    dragRef.current = { kind: 'move', index: visualSections.findIndex((s) => s.id === sid) };
    setDragging(true);
  }
  // Dropped on the canvas: add a new component or reorder an existing section,
  // inserting before `beforeId` (null = append). Reads the active drag payload.
  function canvasDrop(beforeId: string | null) {
    const d = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    if (!d || readOnly) return;
    const at = beforeId ? visualSections.findIndex((s) => s.id === beforeId) : -1;
    const insertAt = at >= 0 ? at : visualSections.length;
    if (d.kind === 'add') {
      if (!visualAddable.includes(d.type)) return;
      setRailOpen(false);
      if (d.type === 'footer') { addFooter(); return; }
      const sec = newSectionFilled(d.type, mkId());
      setVisualSections([...visualSections.slice(0, insertAt), sec, ...visualSections.slice(insertAt)]);
      select(sec.id);
      glow(sec.id);
      openSheetIfNarrow();
      return;
    }
    const from = d.index;
    if (from < 0 || from === insertAt || from + 1 === insertAt) return; // dropped in place
    const arr = [...visualSections];
    const [item] = arr.splice(from, 1);
    arr.splice(from < insertAt ? insertAt - 1 : insertAt, 0, item);
    setVisualSections(arr);
    if (item) glow(item.id);
  }
  // A step (or the thank-you row) in the rail: the canvas switches to it and
  // the selection clears.
  function selectStep(i: number) { setActiveStep(i); setPreviewThanks(false); select(null); }
  function selectThanks() { setPreviewThanks(true); select(null); }
  // A section clicked on the canvas: selected, settings on the Section tab. On
  // phones the sheet stays closed (the "Edit hero" pill opens it).
  function selectSection(sid: string) { select(sid); }
  // A section picked in the rail: on phones the drawer closes and its settings open.
  function pickSection(sid: string) {
    select(sid);
    if (isNarrow()) { setRailOpen(false); setSheetOpen(true); }
  }
  /** Patches one section, found by id in any step, the thank-you or the footer,
   *  against the page as it is NOW (a functional update, not this render's
   *  copy): a late callback, like an image upload finishing after more edits,
   *  only touches its own fields and never brings back older words. */
  function patchSection(sid: string, p: Partial<Section>) {
    setPage((cur) => {
      if (!cur) return cur;
      const c = cur.content;
      if (c.footer?.id === sid) return { ...cur, content: { ...c, footer: { ...c.footer, ...p } } };
      const apply = (list: Section[]) => list.map((sx) => (sx.id === sid ? { ...sx, ...p } : sx));
      const nextSteps = (c.steps ?? []).map((st) => ({ ...st, sections: apply(st.sections ?? []) }));
      const next: PageContent = { ...c, steps: nextSteps, sections: nextSteps.length ? nextSteps[0].sections : apply(c.sections ?? []) };
      if (c.thank_you?.sections) next.thank_you = { ...c.thank_you, sections: apply(c.thank_you.sections) };
      return { ...cur, content: next };
    });
  }
  function updateSelected(p: Partial<Section>) {
    if (!selectedId || readOnly) return;
    patchSection(selectedId, p);
  }
  // Inline canvas edits (contentEditable commit-on-blur), keyed by the section
  // id the canvas reports (footer and thank-you sections included).
  function editSectionText(sid: string, p: Partial<Section>) {
    if (readOnly) return;
    patchSection(sid, p);
  }
  function visMove(sid: string, dir: -1 | 1) {
    const i = visualSections.findIndex((sx) => sx.id === sid);
    if (i < 0) return;
    const next = moveItem(visualSections, i, i + dir);
    if (next === visualSections) return;
    setVisualSections(next);
    glow(sid);
  }
  // The canvas toolbar's Up / Down when hidden sections sit in between: the
  // section lands at the index of the visible neighbor it swaps with.
  function visMoveTo(sid: string, to: number) {
    const i = visualSections.findIndex((sx) => sx.id === sid);
    if (i < 0) return;
    setVisualSections(moveItem(visualSections, i, to));
    glow(sid);
  }
  function visDup(sid: string) {
    const i = visualSections.findIndex((sx) => sx.id === sid);
    if (i < 0) return;
    const copy = duplicateSection(visualSections[i], mkId());
    setVisualSections([...visualSections.slice(0, i + 1), copy, ...visualSections.slice(i + 1)]);
    select(copy.id);
    glow(copy.id);
  }
  // "Hide from page" / "Show on page": a hidden section stays in the page but
  // is not shown. Shown again, the flag is dropped (absent, never false).
  function toggleHidden(sid: string) {
    setVisualSections(visualSections.map((sx) => {
      if (sx.id !== sid) return sx;
      const { hidden, ...rest } = sx;
      return hidden ? rest : { ...rest, hidden: true };
    }));
  }
  function visDelete(sid: string) {
    if (sid === footerId) { setContent({ footer: undefined }); select(null); return; }
    const i = visualSections.findIndex((sx) => sx.id === sid);
    if (i < 0) return;
    const next = visualSections.filter((_, k) => k !== i);
    setVisualSections(next);
    select(next[i]?.id ?? null); // the next sibling takes the slot, or none
  }
  function visAdd(type: SectionType) {
    const after = addAnchor?.after ?? null;
    setAddAnchor(null);
    if (type === 'footer') { addFooter(); return; }
    const sec = newSectionFilled(type, mkId());
    const i = after ? visualSections.findIndex((sx) => sx.id === after) : -1;
    setVisualSections(i >= 0
      ? [...visualSections.slice(0, i + 1), sec, ...visualSections.slice(i + 1)]
      : [...visualSections, sec]);
    select(sec.id); // selecting scrolls it into view in the canvas
    glow(sec.id);
    openSheetIfNarrow();
  }
  // Opens the add dialog to insert after `after` (null = at the end). The rail
  // drawer and the sheet close first so the dialog is never hidden behind them.
  function openAdd(after: string | null) {
    if (readOnly) return;
    setRailOpen(false);
    setSheetOpen(false);
    setAddAnchor({ after });
  }
  // The rail's "+" and "Add section": after the selected section, or at the end.
  function addAfterSelection() { openAdd(selectedId && selIdx >= 0 ? selectedId : null); }

  /** Asks the server whether `slug` is free (answers to older keystrokes are dropped). */
  async function checkSlugAvailable(slug: string) {
    if (!isValidSlug(slug)) return;
    const n = ++slugReq.current;
    try {
      const r = await api.slugAvailable(slug, page!.id);
      if (n === slugReq.current) setSlugCheck({ slug, available: !!r.available, reason: r.reason });
    } catch { /* the save reports a real conflict */ }
  }
  function onSlugChange(slug: string) {
    patch({ slug });
    void checkSlugAvailable(slug);
  }
  const slugMsg = slugInvalid
    ? { text: SLUG_PROBLEM.invalid, tone: 'text-danger' }
    : slugProblem
      ? { text: SLUG_PROBLEM[slugProblem], tone: 'text-danger' }
      : slugChanged && slugCheck?.slug === page.slug && slugCheck.available
        ? { text: 'Available', tone: 'text-success' }
        : null;

  // ── Forms ───────────────────────────────────────────────────────────
  const linkedForm = resolveLinkedForm(forms, page.form_id, page.form);
  // A live page whose form is still a draft shows no form to visitors.
  // Publishing changes fixes it, so the button stays available for it.
  const formNotLive = live && linkedForm?.status === 'draft';
  const canPublishChanges = saver.dirty || formNotLive;

  async function unpublish() {
    if (!(await confirm({ title: 'Take this page offline?', body: 'Visitors will see that it isn’t available.', confirmLabel: 'Unpublish', danger: true }))) return;
    await saver.run('unpublish');
  }
  // Back to the list. A draft writes its pending edits and goes; a live page
  // with unpublished edits (or a draft that couldn't save) asks first.
  async function leave() {
    await saver.whenIdle(); // a publish still going isn't "unsaved"
    if (canWrite && saver.isDirty()) {
      if (!live && !blocked && !saver.stale && (await saver.run('save')) && !saver.isDirty()) { setLeaveGuard(null); navigate('/pages'); return; }
      const ok = await confirm(live
        ? { title: 'Leave without publishing?', body: 'Your changes aren’t live yet and will be lost.', confirmLabel: 'Leave', danger: true }
        : { title: 'Leave without saving?', body: 'Your latest changes will be lost.', confirmLabel: 'Leave', danger: true });
      if (!ok) return;
    }
    setLeaveGuard(null);
    navigate('/pages');
  }

  async function doSaveTemplate(nm: string) {
    if (!nm) return;
    setTplOpen(false);
    try {
      // The linked form's questions travel with the template: a page made
      // from it gets its own draft copy of the form.
      let form: { questions: unknown[]; settings: Record<string, unknown> } | null = null;
      if (page!.form_id != null && linkedForm && linkedForm.status !== 'archived') {
        const f = await api.getCrmForm(page!.form_id).catch(() => null);
        if (f) form = { questions: Array.isArray(f.questions) ? f.questions : [], settings: f.settings ?? {} };
      }
      // Snapshot the WHOLE multi-step content (steps + mirrored sections + thank-you + footer).
      await api.saveTemplate({ name: nm, content: syncLegacySections(page!.content), theme: page!.theme, form });
      toast('Saved as a template');
    } catch { toast('Couldn’t save the template. Try again.', 'error'); }
  }

  // Opens a live render of the page in a new tab via its private `?pt=` link.
  // The render comes from the saved row, so a draft writes its pending edits
  // first; a live page with unpublished edits says the preview shows the
  // live version. The token is minted once on the server and kept on the
  // page. The tab opens right away, while the click still counts as a user
  // action for popup blockers, and is pointed at the preview once the save and
  // token are ready.
  async function openPreview() {
    if (previewRequest.current) return;
    previewRequest.current = true;
    setPreviewBusy(true);
    setPreviewLink(null);
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      let slug = savedSlug;
      if (saver.isDirty()) {
        if (!live && canWrite) {
          // A failed save must not open an older page as if it were the new preview.
          if (!(await saver.run('save'))) {
            tab?.close();
            if (mounted.current) toast('Save your changes before opening the preview. Check the save message above.', 'error');
            return;
          }
          if (saver.isDirty()) {
            tab?.close();
            if (mounted.current) toast('Your page changed while saving. Open Preview again to include the latest changes.', 'info');
            return;
          }
          slug = pageRef.current?.slug ?? slug;
        } else if (live) toast('Preview shows the live page. Publish changes to see your edits there.', 'info');
      }
      if (!mounted.current) { tab?.close(); return; }
      let token = pageRef.current?.preview_token;
      if (!token) {
        token = (await api.ensurePreviewToken(page!.id)).token;
        if (!mounted.current) { tab?.close(); return; }
        patch({ preview_token: token });
      }
      const url = `${window.location.origin}/p/${encodeURIComponent(slug)}?pt=${encodeURIComponent(token)}`;
      if (tab) { if (!tab.closed) tab.location.href = url; }
      else setPreviewLink(url);
    } catch { tab?.close(); if (mounted.current) toast('Couldn’t open the preview. Try again.', 'error'); }
    finally { previewRequest.current = false; if (mounted.current) setPreviewBusy(false); }
  }

  // "New form" in the Form-section inspector: a draft CRM form named after the
  // page, linked at once. It goes live with the page; its questions are
  // edited in the CRM.
  async function createPageForm() {
    if (formBusy || readOnly) return;
    setFormBusy(true);
    try {
      const f = await api.createCrmForm(`${page!.name.trim() || 'Page'} form`);
      setForms((cur) => [f, ...(cur ?? []).filter((x) => x.id !== f.id)]);
      patch({ form_id: f.id });
      toast(`Made the draft form “${f.name}”`, 'success', { label: 'Edit form', onAction: () => { window.open(crmFormEditorPath(f.id), '_blank', 'noopener'); } });
    } catch (e) { toast((e as Error).message || 'Couldn’t create the form. Try again.', 'error'); }
    finally { setFormBusy(false); }
  }

  // Version history: a restore is a normal edit, so ⌘Z brings the page back.
  // The page as it is now is kept as a version first.
  function restoreVersion(v: PageVersion, n: number) {
    void history.saveVersion('Before restore');
    history.revertTo(v);
    setHistoryOpen(false);
    toast(`Restored version ${n}.`, 'info');
    const slug = v.snapshot?.slug;
    if (slug && slug !== savedSlug) void checkSlugAvailable(slug);
  }
  function openHistory() {
    setHistoryOpen(true);
    void history.loadVersions();
  }

  // "Try it": the page behaves like the live one but nothing is sent. '#next'
  // and a form set to move on go to the next step; a submit on the last step
  // shows the thank-you page. What would have happened is said in a toast.
  const lastStep = previewThanks || stepIdx >= steps.length - 1;
  const tryOptions: TryItOptions = {
    onNotice: (message) => toast(message, 'info'),
    onNextStep: lastStep ? undefined : () => setActiveStep(stepIdx + 1),
    onThankYou: !previewThanks ? () => setPreviewThanks(true) : undefined,
    lastStep,
  };

  // The canvas shows the linked form even while it is a draft (staff preview).
  const canvasForm = linkedForm && linkedForm.status !== 'archived' && linkedForm.slug ? { slug: linkedForm.slug, name: linkedForm.name } : null;
  const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const appHost = typeof window !== 'undefined' ? window.location.host : '';
  const domainList: PageDomain[] = domains?.domains ?? [];
  const hosts = [appHost, ...domainList.filter((d) => d.status === 'active').map((d) => d.domain)];
  const shownHost = hosts.includes(previewHost) ? previewHost : appHost;
  // SEO + social-share: the owner's custom values, with the auto-derived fallback
  // shown as placeholders + in the live preview when a field is left blank.
  const seo = theme.seo ?? {};
  const seoFallback = resolvePageSeo(content, theme, page.name);
  // Against the page as it is now (a late image upload must not bring back older SEO words).
  const setSeo = (p: Partial<NonNullable<PageTheme['seo']>>) =>
    setPage((cur) => (cur ? { ...cur, theme: { ...cur.theme, seo: { ...(cur.theme?.seo ?? {}), ...p } } } : cur));
  const formSection = visualSections.find((sx) => sx.type === 'form') ?? null;

  // ── Inspector panes ─────────────────────────────────────────────────
  // Section: the selected section's settings (head row with hide, duplicate
  // and delete; the form group; its fields; Style), or a short "Select a
  // section" state. Page: the page design and settings. AI: the AI panel.
  const sectionPane = selectedSection ? (
    <div data-section-pane>
      <SectionTab section={selectedSection} isFooter={selectedIsFooter} onChange={updateSelected}
        canPasteStyle={copiedStyle !== null}
        onCopyStyle={() => { if (readOnly) return; setCopiedStyle(sectionStylePatch(selectedSection)); toast('Style copied.'); }}
        onPasteStyle={() => { if (readOnly || !copiedStyle) return; history.checkpoint(); updateSelected(sectionStylePatch(copiedStyle)); toast('Style applied.'); }}
        onResetStyle={() => { if (readOnly) return; history.checkpoint(); updateSelected(resetSectionStylePatch()); }}
        multiStep={steps.length > 1} lastStep={previewThanks || stepIdx >= steps.length - 1}
        onToggleHidden={() => toggleHidden(selectedSection.id)}
        onDuplicate={() => visDup(selectedSection.id)} onDelete={() => visDelete(selectedSection.id)}
        forms={forms} formId={page.form_id} linkedForm={page.form ?? null} onFormChange={(fid) => patch({ form_id: fid })}
        onNewForm={() => { void createPageForm(); }} formBusy={formBusy} pageLive={live} />
    </div>
  ) : (
    <div data-empty-section><SectionTabEmpty onPageDesign={() => setInspectorTab('page')} /></div>
  );

  const pagePane = (
    <div data-page-pane>
      <PageTab page={page} theme={theme} setTheme={setTheme} patch={patch}
        onSlugChange={onSlugChange} slugMsg={slugMsg} slugInvalid={slugInvalid} slugTaken={!!slugProblem}
        revealLinkSignal={revealLink}
        revealTrackingSignal={revealTracking}
        seo={seo} seoFallback={seoFallback} setSeo={setSeo}
        host={shownHost} hosts={hosts} onHostChange={setPreviewHost}
        selectedForm={linkedForm} pageLive={live} onChangeForm={formSection ? () => pickSection(formSection.id) : undefined} />
    </div>
  );

  // AI tab: the answer is applied to the step (or thank-you page) it was asked
  // about, as that step is when the answer arrives, so edits made while the AI
  // worked are kept. A version is saved first; Undo also brings it back.
  const aiScope = { thanks: previewThanks, stepId: steps[stepIdx]?.id ?? null };
  function applyAiResult(scope: typeof aiScope, result: unknown, summary: string) {
    void history.saveVersion('Before AI edit');
    // Ids for any sections the AI adds, the same in the dry run and the real
    // apply, so what changed (new sections included) can glow.
    const fresh = Array.from({ length: 30 }, () => mkId());
    const ids = () => { let k = 0; return () => fresh[k++] ?? mkId(); };
    const cur = pageRef.current?.content;
    const before = scope.thanks ? cur?.thank_you?.sections ?? [] : cur?.steps?.find((st) => st.id === scope.stepId)?.sections ?? [];
    const old = new Map(before.map((sx) => [sx.id, JSON.stringify(sx)]));
    glow(...applyAiChanges(before, result, ids()).sections.filter((sx) => old.get(sx.id) !== JSON.stringify(sx)).map((sx) => sx.id));
    setPage((p) => {
      if (!p) return p;
      const c = p.content;
      if (scope.thanks) {
        const ty = applyAiChanges(c.thank_you?.sections ?? [], result, ids()).sections;
        return { ...p, content: { ...c, thank_you: { ...c.thank_you, sections: ty } } };
      }
      const list = c.steps ?? [];
      const i = list.findIndex((st) => st.id === scope.stepId);
      if (i < 0) return p;
      const next = list.map((st, k) => (k === i ? { ...st, sections: applyAiChanges(st.sections, result, ids()).sections } : st));
      return { ...p, content: { ...c, steps: next, sections: next[0]?.sections ?? [] } };
    });
    toast(summary ? `AI: ${summary}` : 'AI edit done. Undo brings the old words back.', 'info');
  }
  const aiPane = (
    <div data-ai-pane>
      <AiPanel sections={visualSections} pageName={page.name} disabled={readOnly}
        scopeLabel={previewThanks ? 'the thank-you page' : steps.length > 1 ? 'this step' : 'this page'}
        onApply={(result, summary) => applyAiResult(aiScope, result, summary)} />
    </div>
  );

  // Arrow keys move between the inspector tabs (and focus follows).
  function onTabsKey(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const at = INSPECTOR_TABS.findIndex((t) => t.key === inspectorTab);
    const next = INSPECTOR_TABS[(at + (e.key === 'ArrowRight' ? 1 : -1) + INSPECTOR_TABS.length) % INSPECTOR_TABS.length].key;
    setInspectorTab(next);
    document.getElementById(`${uid}-tab-${next}`)?.focus();
  }

  // ── Canvas ──────────────────────────────────────────────────────────
  const editing = mode === 'edit';
  // The page on the canvas: click-to-edit in Edit mode; in Try it, a visitor's
  // view (no selection chrome, '#next' moves on, nothing is sent). View only:
  // sections still select (to read their settings), nothing else.
  const canvasPage = (
    <PageView content={content} theme={theme} form={canvasForm} pageId={page.id} preview showThankYou={previewThanks} sections={sections}
      stack={device === 'phone'}
      merge={editing ? undefined : {}}
      editable selectedId={editing ? selectedId : null} onSelect={selectSection} onDeselect={deselect} readOnly={readOnly}
      {...(readOnly ? {} : {
        onMove: visMove, onMoveTo: visMoveTo, onDuplicate: visDup, onDelete: visDelete,
        onAddBelow: (sid: string) => openAdd(sid), onEditText: editSectionText,
        canvasDragging: dragging, onCanvasDrop: canvasDrop, onCanvasDragStart: canvasDragStart,
      })}
      flashIds={editing ? flash.ids : undefined} flashKey={flash.n}
      interactive={editing ? undefined : tryOptions} />
  );

  // The one place the canvas renders: the gray stage (a click on it clears the
  // selection) around the page at the chosen size.
  const canvas = (
    <CanvasStage device={device} mode={mode} onBackgroundClick={deselect} className="min-h-0 flex-1">
      {canvasPage}
    </CanvasStage>
  );

  // ── Rail ────────────────────────────────────────────────────────────
  // The same rail shows in the left column and, below 1024px, in the drawer.
  const rail = (
    <EditorRail
      steps={steps} activeStep={stepIdx} thanks={previewThanks} readOnly={readOnly}
      onSelectStep={selectStep} onSelectThanks={selectThanks}
      onAddStep={addStep}
      onRenameStep={(i) => setRenameIdx(i)}
      onMoveStep={(i, dir) => moveStep(dir, i)}
      onDuplicateStep={(i) => dupStep(i)}
      onDeleteStep={(i) => { void delStep(i); }}
      sections={visualSections} footer={content.footer ?? null} selectedId={selectedId}
      onSelectSection={pickSection} onMoveSection={visMove} onDuplicateSection={visDup} onDeleteSection={visDelete}
      onToggleHidden={toggleHidden} onRemoveFooter={() => { if (footerId) visDelete(footerId); }}
      onAddSection={addAfterSelection}
      dragging={dragging} dropAt={dropAt}
      onDragStartRow={railDragStart} onDragOverIndex={setDropAt} onDropAt={dropSection} onDragEnd={dragDone}
      flashIds={flash.ids} flashKey={flash.n} />
  );

  // ── Header ──────────────────────────────────────────────────────────
  // Save status: errors stay visible at every width (with a retry, a reload,
  // or a way to the field to fix); the quiet states show their words on wider
  // screens.
  const err = saver.error;
  const busyStatus = saver.running === 'publish' || saver.running === 'unpublish';
  const saveStatus: SaveStatus = err ? { kind: 'error', message: err.message, retryable: err.retryable, stale: err.kind === 'stale' }
    : saver.running === 'publish' ? { kind: 'publishing' }
    : saver.running === 'unpublish' ? { kind: 'unpublishing' }
    : saver.running ? { kind: 'saving' }
    : saver.dirty ? { kind: 'dirty' }
    : formNotLive ? { kind: 'formNotLive' }
    : { kind: 'saved' };
  const stepLabel = previewThanks ? 'Thank you' : steps[stepIdx]?.name || `Step ${stepIdx + 1}`;

  return (
    <div className="pe-scope pe-root" data-page-editor data-readonly={readOnly || undefined}
      onKeyDownCapture={(e) => {
        // ⌘S typed inside the canvas iframe (its document has its own key
        // events; React still delivers them here through the portal).
        const t = e.target as Node;
        if (t.ownerDocument === document || !isSaveCombo(e.nativeEvent)) return;
        e.preventDefault();
        runShortcut(e.target);
      }}>
      <EditorHeader
        name={page.name} onNameChange={(name) => patch({ name })} readOnly={readOnly}
        live={live} unpublished={live && saver.dirty}
        stepLabel={stepLabel} onOpenRail={() => setRailOpen(true)}
        device={device} onDeviceChange={setDevice} mode={mode} onModeChange={setMode}
        canUndo={history.canUndo} canRedo={history.canRedo} onUndo={history.undo} onRedo={history.redo}
        onOpenHistory={openHistory} onOpenAnalytics={() => setAnalyticsOpen(true)} onOpenShare={() => setShareOpen(true)}
        onPreview={() => { void openPreview(); }} previewBusy={previewBusy}
        save={saveStatus}
        onSaveNow={!live && saver.dirty && !saver.running && !saver.stale ? () => { void saver.run('save'); } : undefined}
        onRetry={saver.retry} onFixError={revealPageSettings} onReload={onReload}
        publishLabel={saver.running === 'publish' ? 'Publishing…' : live ? 'Publish changes' : 'Publish'}
        publishDisabled={busyStatus || saver.stale || (live && !canPublishChanges)}
        publishTitle={live ? (canPublishChanges ? 'Make your changes live (⌘S)' : 'No changes to publish') : undefined}
        onPublish={() => { void publish(); }}
        onSaveTemplate={() => setTplOpen(true)}
        onUnpublish={live ? () => { void unpublish(); } : undefined} unpublishDisabled={busyStatus}
        onBack={() => { void leave(); }} />

      <div className="pe-body">
        <aside className="pe-rail" aria-label="Pages and sections">{rail}</aside>

        <main className="pe-canvas">
          {previewLink && <div className="pe-preview-notice" role="status"><span>Your browser blocked the preview window.</span><a href={previewLink} target="_blank" rel="noopener noreferrer">Open preview</a><button type="button" onClick={() => setPreviewLink(null)} aria-label="Dismiss preview notice">Dismiss</button></div>}
          {canvas}
          {selectedSection && (
            <PhoneEditPill label={readOnly ? `${typeLabelInline(selectedSection.type)} settings` : `Edit ${typeLabelInline(selectedSection.type)}`}
              onClick={() => openSheet('section')} />
          )}
        </main>

        {/* Below 1024px the inspector is a bottom sheet over this overlay. */}
        {sheetOpen && <div className="pe-sheet-overlay" onClick={() => setSheetOpen(false)} aria-hidden="true" />}
        <aside data-inspector data-tab={inspectorTab} className={`pe-inspector${sheetOpen ? ' is-open' : ''}`} aria-label="Settings">
          <div className="pe-inspector-tabs">
            <div className="seg pe-seg pe-glide-host flex w-full rounded-lg" role="tablist" aria-label="Settings panels" onKeyDown={onTabsKey}>
              <SegGlide active={Math.max(0, INSPECTOR_TABS.findIndex((t) => t.key === inspectorTab))} />
              {INSPECTOR_TABS.map((t) => (
                <button key={t.key} type="button" role="tab" data-glide-item id={`${uid}-tab-${t.key}`} aria-selected={inspectorTab === t.key}
                  aria-controls={`${uid}-panel`} tabIndex={inspectorTab === t.key ? 0 : -1}
                  className={`seg-item pe-seg-text flex-1 justify-center ${inspectorTab === t.key ? 'seg-item-active' : ''}`}
                  onClick={() => setInspectorTab(t.key)}>
                  {t.icon}{t.label}
                </button>
              ))}
            </div>
            <button type="button" className="pe-icon-btn pe-inspector-close" aria-label="Close settings" onClick={() => setSheetOpen(false)}>
              <ChevronDownIcon size={16} />
            </button>
          </div>
          <div className="pe-inspector-body" role="tabpanel" id={`${uid}-panel`} aria-labelledby={`${uid}-tab-${inspectorTab}`}>
            {/* Keyed by what it shows, so a new tab or section eases in. View
                only: the panel sits in a disabled fieldset (every control
                off) and its groups stay open so it can still be read. */}
            <div className="pe-pane" key={inspectorTab === 'section' ? `section:${selectedId ?? ''}` : inspectorTab}>
              <InspectorReadOnly.Provider value={readOnly}>
                <fieldset className="pe-readonly" disabled={readOnly}>
                  {inspectorTab === 'section' ? sectionPane : inspectorTab === 'page' ? pagePane : aiPane}
                </fieldset>
              </InspectorReadOnly.Provider>
            </div>
          </div>
        </aside>
      </div>

      <PhoneBottomBar onSections={() => setRailOpen(true)} onDesign={() => openSheet('page')} onAi={() => openSheet('ai')} />

      {/* Below 1024px the rail opens from the left. */}
      <EditorDrawer open={railOpen} onClose={() => setRailOpen(false)} title="Pages and sections" side="left" flush>
        {rail}
      </EditorDrawer>

      <HistoryDrawer open={historyOpen} versions={history.versions} loading={history.versionsLoading} error={history.versionsError}
        canRestore={canWrite} onRestore={restoreVersion} onClose={() => setHistoryOpen(false)}
        isCurrent={(v) => snapshotKey(prepareSnapshot(v.snapshot)) === currentSnapshotKey} />

      <ShareDrawer open={shareOpen} onClose={() => setShareOpen(false)} live={live}
        pageId={page.id} pageName={page.name} slug={savedSlug} appOrigin={appOrigin} domains={domainList}
        canPublish={canWrite} canSetHomepage={canSetHomepage}
        onPublish={() => { void publish(); }} publishing={busyStatus}
        settings={{
          readOnly, draftSlug: page.slug, onSlugChange, slugMsg, slugBad: slugInvalid || !!slugProblem,
          seo, seoFallback, setSeo, host: shownHost, hosts, onHostChange: setPreviewHost, unpublished: live && saver.dirty,
        }}
        onDomainChange={(d) => setDomains((cur) => (cur ? {
          ...cur,
          // One page is the homepage of a domain: the domain the panel changed
          // now points here.
          domains: cur.domains.map((x) => (x.id === d.id ? d : x)),
        } : cur))} />

      <AnalyticsDrawer open={analyticsOpen} onClose={() => setAnalyticsOpen(false)}
        pageId={page.id} hasForm={page.form_id != null} published={live}
        onTrackingSettings={() => { setAnalyticsOpen(false); setInspectorTab('page'); if (isNarrow()) setSheetOpen(true); setRevealTracking(Date.now()); }} />

      <PromptDialog open={renameIdx !== null} title="Rename step" placeholder="Step name (e.g. Opt-in)"
        defaultValue={renameIdx !== null ? steps[renameIdx]?.name ?? '' : ''} confirmLabel="Rename"
        onSubmit={renameStep} onClose={() => setRenameIdx(null)} />

      <PromptDialog open={tplOpen} title="Save as template" placeholder="Template name (e.g. Roofing quote funnel)"
        defaultValue={page.name} confirmLabel="Save template"
        onSubmit={(v) => { void doSaveTemplate(v); }} onClose={() => setTplOpen(false)} />

      {/* The add-section picker (rail "+" and "Add section", the canvas "+").
          Its items also drag out onto the rail or the canvas. */}
      <AddSectionDialog open={addAnchor !== null} addable={visualAddable} onAdd={visAdd} onClose={() => setAddAnchor(null)}
        onDragType={paletteDragStart} onDragDone={dragDone} />
    </div>
  );
}
